import React from 'react';
import V2Layout from '../Layout';
import { Box, Button, Card, Grid, InputAdornment, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField } from '@mui/material';
import { translate } from '../../../i18n/customI18nProvider';
import Swal from 'sweetalert2';
import { convertMessageCodeToMessage, optionsToChip } from '../../../util';
import { getRight } from '../../../services/storage';
import { getCategoryDetails, getWristbandPackets, deleteWristbandPacket, generateQrsFile } from '../../../services/wristbandCategory.service';

import SearchIcon from '@mui/icons-material/Search';
import ShowIcon from '@mui/icons-material/Visibility';
import QrIcon from '@mui/icons-material/QrCode';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import { removeUrlParam, setUrlParam } from '../../../services/common.service';
import StyledTableRow from '../../../components/v2/common/table/StyledTableRow';
import {visuallyHidden} from '@mui/utils';
import config from '../../../config';
import { Inventory, Recycling, TableRows, TakeoutDining } from '@mui/icons-material';
import WristbandManualReconditioning from '../../../components/wristbands/WristbandManualReconditioning';

const tableHeader = [
    {
        id: 'shortId',
        key: 'shortId',
        align: 'left',
        disablePadding: false,
        label: translate('custom.short_id'),
        sortable: true
    },
    {
        id: 'quantity',
        key: 'quantity',
        align: 'left',
        disablePadding: false,
        label: translate('custom.quantity'),
        sortable: true
    },
    {
        id: 'range',
        key: 'range',
        align: 'left',
        disablePadding: false,
        label: translate('custom.range'),
        sortable: true
    },
    {
        id: 'importId',
        key: 'importId',
        align: 'left',
        disablePadding: false,
        label: translate('custom.import_id'),
        sortable: true
    },
    {
        id: 'importType',
        key: 'importType',
        align: 'left',
        disablePadding: false,
        label: translate('custom.import_type'),
        sortable: true
    },
    {
        id: 'createdAt',
        key: 'createdAt',
        align: 'left',
        disablePadding: false,
        label: translate('custom.creation_date'),
        sortable: true
    },
    {
        id: 'actions',
        key: 'actions',
        align: 'left',  
        disablePadding: false,
        label: translate('custom.actions'),
        sortable: false
    }
]

const colors = {
    excel: 'success',
    manual: 'info',
}

class ShowWristbandCategory extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            current_right: getRight(),
            loading: false,
            form_error: {},
            name: '',
            uid: '',
            packets: [],
            count: 0,
            skip: 0,
            limit: 50,
            limitOptions: [10, 50, 100, 250, 500],
            sort_key: 'createdAt',
            sort_order: 'DESC',
            search: '',
        }

        this.getPackets = this.getPackets.bind(this);
    }

    async setStateAsync(state) {
        return new Promise((resolve) => {
            this.setState(state, resolve);
        });
    }

    async componentDidMount() {
        let category_id = this.props?.match?.params?.category_id ? this.props.match.params.category_id : null;

        const urlParams = new URLSearchParams(window.location.search);
        const search = urlParams.get('search') ? urlParams.get('search').replaceAll('"', '') : '';
        const import_id = urlParams.get('import_id') ? urlParams.get('import_id').replaceAll('"', '') : '';
        const uid = urlParams.get('uid') ? urlParams.get('uid').replaceAll('"', '') : '';

        await this.setStateAsync({
            search: search,
            import_id: import_id,
            uid: uid,
            loading: true,
            category_id,
        });

        await this.getCategory();

        await this.getPackets();
            
        
        this.setState({ loading: false });
    }   

    async getCategory() {
        let category = await getCategoryDetails(this.state.category_id);
        if (category.error) {
            Swal.fire({
                icon: 'error',
                title: translate('custom.error'),
                text: category.error_code ? convertMessageCodeToMessage(category.error_code) : category.error
            });
            return;
        }

        await this.setStateAsync({
            name: category.name ? category.name : '',
            title: category.name
        });
    }

    async getPackets() {
        let options = {
            skip: this.state.skip,
            limit: this.state.limit
        }

        const filter = {};

        if (this.state.search && this.state.search.length > 0) {
            filter.shortId = this.state.search;
        }

        if (this.state.import_id && this.state.import_id.length > 0) {
            filter['import.id'] = this.state.import_id;
        }

        if (this.state.uid && this.state.uid.length > 0) {
            filter['wristbands.uid'] = this.state.uid;
        }
        
        options.filter = encodeURIComponent(JSON.stringify(filter));
        
        if (this.state.sort_key) {
            options.sort = encodeURIComponent(JSON.stringify([this.state.sort_key, this.state.sort_order]));
        }

        let {packets, total} = await getWristbandPackets(options, this.state.category_id);

        this.setState({
            packets: packets,
            count: total
        });
    }
    
    async handleDeleteWristbandPacket(packet_id) {
        const confirm_delete = await Swal.fire({
            icon: 'warning',
            title: translate('custom.warning'),
            text: translate('custom.are_you_sure_you_want_to_delete') + ' ' + translate('custom.you_will_not_be_able_to_undo_this_action'),
            showCancelButton: true,
            confirmButtonText: translate('custom.delete'),
            cancelButtonText: translate('custom.cancel'),
            confirmButtonColor: config.baseColor
        });
        if (confirm_delete.isConfirmed) {
            let result = await deleteWristbandPacket(this.state.category_id, packet_id);
            if (result.error) {
                Swal.fire({
                    icon: 'error',
                    title: translate('custom.error'),
                    text: result.error.error ? result.error.error : ''
                });
                return;
            }

            Swal.fire({
                icon: 'success',
                title: translate('custom.success'),
                text: translate('custom.deleted_successfully')
            });

            this.getPackets();
        }
    }

    async downloadQrFile(row) {
        const file = await generateQrsFile(this.state.name, [row]);
        const url = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
    
    formatDate(isoDate) {
        if (!isoDate) return '';
        const date = new Date(isoDate);
        return new Intl.DateTimeFormat("fr-FR", {
            dateStyle: "short",
            timeStyle: "medium",
        }).format(date);
    }

    render() {
        return (
            <V2Layout
                currentMenu='wristbands'
                title={this.state.title}
            >

                {(this.state.loading) ? (
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress />
                    </Box>
                ) : null}

                <Paper sx={{mt: 2, padding: 2}}>

                    <Grid container spacing={2} style={{marginTop: '5px', marginBottom: '10px', justifyContent: 'space-between'}}>
                        <Grid item>
                            <div style={{display: 'flex', gap: '10px'}}>
                                <TextField
                                    size='small'
                                    variant='outlined'
                                    fullWidth
                                    id="search-input"
                                    label={translate('custom.short_id')}
                                    placeholder={translate('custom.search')}
                                    value={this.state.search}
                                    onChange={async (e) => {
                                        await this.setStateAsync({search: e.target.value});
                                        (e?.target?.value?.length > 0) ? setUrlParam('search', e.target.value) : removeUrlParam('search');
                                        this.getPackets();
                                    }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,

                                    }}
                                />
                                <TextField
                                    size='small'
                                    variant='outlined'
                                    fullWidth
                                    id="import-id-input"
                                    label={translate('custom.import_id')}
                                    placeholder={translate('custom.search')}
                                    value={this.state.import_id}
                                    onChange={async (e) => {
                                        await this.setStateAsync({import_id: e.target.value});
                                        (e?.target?.value?.length > 0) ? setUrlParam('import_id', e.target.value) : removeUrlParam('import_id');
                                        this.getPackets();
                                    }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,

                                    }}
                                />
                                <TextField
                                    size='small'
                                    variant='outlined'
                                    fullWidth
                                    id="search-uid-input"
                                    label={translate('custom.uid')}
                                    placeholder={translate('custom.search')}
                                    value={this.state.uid}
                                    onChange={async (e) => {
                                        await this.setStateAsync({uid: e.target.value});
                                        (e?.target?.value?.length > 0) ? setUrlParam('uid', e.target.value) : removeUrlParam('uid');
                                        this.getPackets();
                                    }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,

                                    }}
                                />
                            </div>
                        </Grid>
                        <Grid item>
                            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                <Button variant='contained' style={{textTransform: 'none'}}
                                    startIcon={<Inventory />}
                                    onClick={() => {
                                        this.props.history.push('/v2/admin/wristbands/' + this.state.category_id + '/import');
                                    }}
                                >
                                    {translate('custom.import')}
                                </Button>
                                {/* Accès à la table de reconditionnement fait par le team support sur un événement
                                    <Button variant='contained' style={{textTransform: 'none'}}
                                        startIcon={<Recycling />}
                                        onClick={() => {
                                            this.props.history.push('/v2/admin/wristbands/' + this.state.category_id + '/reconditioning');
                                        }}
                                    >
                                        {translate('custom.reconditioning')}
                                    </Button> 
                                */}
                                <div>
                                    <WristbandManualReconditioning onSuccess={this.getPackets} category_id={this.props?.match?.params?.category_id} />
                                </div>
                                
                            </div>
                        </Grid>
                    </Grid>
                    <TableContainer component={Card} style={{maxHeight: '80vh', overflow: 'auto'}}>
                        <Table stickyHeader size='small'>
                            <TableHead>
                                <TableRow>
                                    {tableHeader.map((headCell) => (
                                        <TableCell
                                            key={headCell.key}
                                            align={headCell.align ? headCell.align : 'left'}
                                            padding={headCell.disablePadding ? 'none' : 'normal'}
                                            sortDirection={(this.state.sort_key === headCell.key) ? ((this.state.sort_order == 'ASC') ? 'asc' : 'desc') : false}
                                        >
                                            {(headCell.sortable) ? (
                                                <TableSortLabel
                                                    active={this.state.sort_key === headCell.key}
                                                    direction={this.state.sort_key === headCell.key ? ((this.state.sort_order == 'ASC') ? 'asc' : 'desc') : 'asc'}
                                                    onClick={async () => {
                                                        await this.setState({
                                                            sort_key: headCell.key,
                                                            sort_order: this.state.sort_key === headCell.key ? (this.state.sort_order === 'ASC' ? 'DESC' : 'ASC') : 'ASC'
                                                        })
                                                        this.getPackets();
                                                    }}
                                                >
                                                    {headCell.label}
                                                    {this.state.sort_key === headCell.key ? (
                                                        <Box component="span" sx={visuallyHidden}>
                                                            {this.state.sort_order === 'DESC' ? 'sorted descending' : 'sorted ascending'}
                                                        </Box>
                                                    ) : null}
                                                </TableSortLabel>
                                            ) : (
                                                <>
                                                    {headCell.label}
                                                </>
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(this.state.packets && Array.isArray(this.state.packets)) ? this.state.packets.map((row) => (
                                    <StyledTableRow key={row._id} hover
                                        onDoubleClick={() => {
                                            this.props.history.push('/v2/admin/wristbands/' + this.state.category_id + '/show/' + row._id + '/show');
                                        }}
                                    >
                                        <TableCell>{row.shortId}</TableCell>
                                        <TableCell>{row.quantity}</TableCell>
                                        <TableCell>{row.minNumber && row.maxNumber ? row.minNumber + " - " + row.maxNumber : "-"}</TableCell>
                                        <TableCell>{row.import?.id}</TableCell>
                                        <TableCell>{optionsToChip(translate("custom." + row.import?.type), colors[row.import?.type])}</TableCell>
                                        <TableCell>{this.formatDate(row.createdAt)}</TableCell>
                                        <TableCell>
                                            <IconButton size='small' onClick={() => {
                                                this.props.history.push('/v2/admin/wristbands/' + this.state.category_id + '/show/' + row._id + '/show');
                                            }}>
                                                <ShowIcon />
                                            </IconButton>
                                            <IconButton size='small' onClick={() => {
                                                this.downloadQrFile(row);
                                            }}>
                                                <QrIcon />
                                            </IconButton>
                                            <IconButton size='small' onClick={() => {
                                                this.handleDeleteWristbandPacket(row._id);
                                            }}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </StyledTableRow>
                                ))
                                :
                                <TableRow>
                                    <TableCell colSpan={tableHeader.length} align="center" style={{padding: '20px'}}>
                                        {translate('custom.no_data')}
                                    </TableCell>
                                </TableRow>
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Grid container style={{width: '100%'}}>
                        <Grid item xs={12}> 
                            <TablePagination
                                component="div"
                                count={this.state.count}
                                page={this.state.skip / this.state.limit}
                                onPageChange={async (event, page) => {
                                    await this.setStateAsync({
                                        skip: page * this.state.limit
                                    })
                                    this.getPackets();
                                }}
                                rowsPerPage={this.state.limit}
                                onRowsPerPageChange={async (event) => {
                                    await this.setStateAsync({
                                        limit: event.target.value
                                    })
                                    this.getPackets();
                                }}
                                rowsPerPageOptions={this.state.limitOptions}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </V2Layout>
        );
    }
}

export default ShowWristbandCategory;